import { ActionType } from 'typesafe-actions';
import * as actions from './comparisonActions';
import {
    ADD_COMPARISON_SELECTED_ITEM,
    GET_COMPARISON_SELECTED_FAILURE,
    GET_COMPARISON_SELECTED_REQUEST,
    GET_COMPARISON_SELECTED_SUCCESS,
    GET_SELECTED_LABELS_FAILURE,
    GET_SELECTED_LABELS_REQUEST,
    GET_SELECTED_LABELS_SUCCESS,
    INIT_COMPARISON_STATE,
    REMOVE_COMPARISON_SELECTED_ITEM,
    REMOVE_COMPARISON_VIEW_ITEM,
    SET_SELECTED_ITEMS,
} from './comparisonConstants';
import { HydratedCurriculumType } from '../Common/types/AppConfigTypes';

export type ComparisonAction = ActionType<typeof actions>;

export interface Selected {
    uuid: string;
    label: string;
}

type ComparisonStateMutable = {
    items: { uuid: string }[];
    isError: boolean;
    isLoading: boolean;
    selected: {
        [activeCTSlug: string]: Selected[];
    };
};

export type ComparisonState = Readonly<ComparisonStateMutable>;

export const initialState = {
    items: [],
    isError: false,
    isLoading: false,
    selected: {},
};

const initComparisonState = (curriculumTypes: HydratedCurriculumType[]) => {
    const state: ComparisonStateMutable = initialState;

    curriculumTypes.forEach(ct => {
        state.selected[ct.slug] = [];
    });

    return state;
};

const updatedSelectedState = (
    activeCTSlug: string,
    updatedSelected: Selected[],
    currentState: ComparisonState,
): ComparisonState => {
    return {
        ...currentState,
        selected: {
            ...currentState.selected,
            [activeCTSlug]: updatedSelected,
        },
    };
};

export default (
    state: ComparisonState = initialState,
    action: ComparisonAction,
) => {
    switch (action.type) {
        case INIT_COMPARISON_STATE: {
            return initComparisonState(action.payload);
        }
        case ADD_COMPARISON_SELECTED_ITEM: {
            const {
                activeCTSlug,
                contentRevisionUuid,
                contentRevisionLabel,
            } = action.payload;
            const currentSelected = state.selected[activeCTSlug];

            const newSelected = {
                uuid: contentRevisionUuid,
                label: contentRevisionLabel,
            };

            return updatedSelectedState(
                activeCTSlug,
                [...currentSelected, newSelected],
                state,
            );
        }
        case REMOVE_COMPARISON_SELECTED_ITEM: {
            const { activeCTSlug, contentRevisionUuid } = action.payload;
            const currentSelected = state.selected[activeCTSlug];

            return updatedSelectedState(
                activeCTSlug,
                currentSelected.filter(
                    ({ uuid }) => uuid !== contentRevisionUuid,
                ),
                state,
            );
        }
        case SET_SELECTED_ITEMS: {
            const { activeCTSlug, selectedUuids } = action.payload;

            const selected = selectedUuids.map(uuid => ({ uuid, label: '' }));

            return updatedSelectedState(activeCTSlug, selected, state);
        }
        case REMOVE_COMPARISON_VIEW_ITEM: {
            return {
                ...state,
                items: state.items.filter(item => item.uuid !== action.payload),
            };
        }
        case GET_COMPARISON_SELECTED_REQUEST:
            return {
                ...state,
                isLoading: true,
            };
        case GET_COMPARISON_SELECTED_SUCCESS:
            return {
                ...state,
                isLoading: false,
                items: action.payload,
                isError: false,
            };
        case GET_COMPARISON_SELECTED_FAILURE:
            return {
                ...state,
                isLoading: false,
                isError: true,
            };
        case GET_SELECTED_LABELS_REQUEST:
            return {
                ...state,
                isLoading: true,
            };
        case GET_SELECTED_LABELS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                isError: false,
                selected: {
                    [action.payload.activeCTSlug]: action.payload.selected,
                },
            };
        case GET_SELECTED_LABELS_FAILURE:
            return {
                ...state,
                isLoading: false,
                isError: true,
            };
        default:
            return state;
    }
};
